import { AssociationDetails } from '@/modules/entity/components/association/AssociationDetails.tsx';
import { AdvisorAccess } from '@/modules/entity/components/common/AdvisorAccess.tsx';
import {
  AdviserDetails,
  AdvisorDocType,
} from '@/modules/entity/components/common/AdvisorDetails.tsx';
import { CompletionInformation } from '@/modules/entity/components/common/CompletionInformation.tsx';
import { Confirmation } from '@/modules/entity/components/common/Confirmation.tsx';
import { ContactDetails } from '@/modules/entity/components/common/ContactDetails.tsx';
import { IdentificationDocuments } from '@/modules/entity/components/common/IdentificationDocuments.tsx';
import { MappedTrustee } from '@/modules/entity/components/common/MappedTrustee.tsx';
import { OptionalSteps } from '@/modules/entity/components/common/OptionalSteps.tsx';
import { PaymentInstructions } from '@/modules/entity/components/common/PaymentInstructions.tsx';
import { PersonIdentificationDocuments } from '@/modules/entity/components/common/PersonIdentificationDocuments.tsx';
import { PersonIndividualDetails } from '@/modules/entity/components/common/PersonIndividualDetails.tsx';
import { TaxInformation } from '@/modules/entity/components/common/TaxInformation.tsx';
import {
  WholesaleCertificate,
  WholesaleCertificateDocType,
} from '@/modules/entity/components/common/WholesaleCertificate.tsx';
import { CompanyCRS } from '@/modules/entity/components/company/CompanyCRS.tsx';
import { CompanyDetails } from '@/modules/entity/components/company/CompanyDetails.tsx';
import { JointlyContactDetails } from '@/modules/entity/components/individual/jointly/JointlyContactDetails.tsx';
import {
  JointlyDocType,
  JointlyIdentificationDetails,
} from '@/modules/entity/components/individual/jointly/JointlyIdentificationDocs.tsx';
import { JointlyInvestorDetailsPage } from '@/modules/entity/components/individual/jointly/JointlyInvestorDetailsPage.tsx';
import { JointlyTaxInformation } from '@/modules/entity/components/individual/jointly/JointlyTaxInformation.tsx';
import { PartnershipDetails } from '@/modules/entity/components/partnership/PartnershipDetails.tsx';
import { SmsfDetails } from '@/modules/entity/components/smsf/SmsfDetails.tsx';
import { TrustDetails } from '@/modules/entity/components/trust/TrustDetails.tsx';
import {
  OnboardingRoadItem,
  getNavProgressShareholder,
} from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { EntityStatus } from 'api';

export const individualRoad: OnboardingRoadItem[] = [
  {
    component: ContactDetails,
    entity_type_prefix: 'individual_entity_details',
    navName: '', //'Contact details',
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.phone_number &&
        !!data?.entity?.entity_name &&
        !!data?.entity?.preferred_contact_way;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: PersonIndividualDetails,
    entity_type_prefix: 'individual_entity_details',
    navName: 'Individual details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.individual_entity_details?.first_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: PersonIdentificationDocuments,
    entity_type_prefix: 'individual_entity_details',
    navName: 'Identification documents',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.individual_entity_details?.passport_number ||
        !!data?.entity?.individual_entity_details?.driver_license_number ||
        !!data?.entity?.individual_entity_details?.medicare_card_number;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: TaxInformation,
    entity_type_prefix: 'individual_entity_details',
    navName: 'Tax information',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = (data?.entity?.individual_entity_details?.tax_residence?.length || 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'individual_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'individual_entity_details',
      field_name: 'is_managed_by_another_institution',
      title: 'Is this entity managed by another Financial Institution?',
      subroad: [CompanyCRS],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Investment entity status',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 1;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const completed =
        !!data?.entity?.individual_entity_details?.crs_status ||
        data?.entity?.individual_entity_details?.is_managed_by_another_institution === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'individual_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'individual_entity_details',
      field_name: 'is_jointly',
      title: 'Are you investing as an individual or investing jointly?',
      subroad: [
        JointlyInvestorDetailsPage,
        JointlyContactDetails,
        JointlyIdentificationDetails,
        JointlyTaxInformation,
      ],
      options: [
        { label: 'Individually', value: false },
        { label: 'Jointly', value: true },
      ],
    },
    navName: 'Individual / Jointly',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 4;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const currentDocuments =
        (data?.entity?.document_files || []).filter(
          item =>
            item.type === JointlyDocType.JointlyPassport ||
            item.type === JointlyDocType.JointlyDriverLicense,
        )?.length > 0;
      const completed =
        (!!data?.entity?.individual_entity_details?.jointly?.first_name &&
          !!data?.entity?.individual_entity_details?.jointly?.phone_number &&
          (data?.entity?.individual_entity_details?.jointly?.tax_residence?.length || 0) > 0 &&
          currentDocuments) ||
        data?.entity?.individual_entity_details?.is_jointly === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: PaymentInstructions,
    entity_type_prefix: 'individual_entity_details',
    navName: 'Payment instructions',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.bank_account_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'individual_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'individual_entity_details',
      field_name: 'is_advisor',
      title: 'Do you want someone else to access this account on your behalf?',
      description: 'For example: an accountant or financial advisor',
      subroad: [AdvisorAccess, AdviserDetails],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Advisor details',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 2;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const document = data?.entity?.document_files?.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      const completed =
        (!!document && !!data?.entity?.individual_entity_details?.name_authorized_representative) ||
        data?.entity?.individual_entity_details?.is_advisor === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'individual_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'individual_entity_details',
      field_name: 'is_wholesale',
      title: 'Are you Wholesale investor?',
      subroad: [WholesaleCertificate],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Wholesale investor details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const document = (data?.entity?.document_files || [])?.filter(
        item => item.type === WholesaleCertificateDocType.Certificate,
      );
      const completed =
        (document?.length > 0 && !!data?.entity?.individual_entity_details?.wholesale_type) ||
        data?.entity?.individual_entity_details?.is_wholesale === false;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: Confirmation,
    entity_type_prefix: 'individual_entity_details',
    navName: 'Confirmation',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.status && data?.entity?.status !== EntityStatus.EntityCreation;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: CompletionInformation,
    navName: 'Completion',
    entity_type_prefix: 'individual_entity_details',
    allowSkip: true,
    getNavProgress: () => {
      return {
        completed: false,
        percent: 100,
      };
    },
  },
];

export const companyRoad: OnboardingRoadItem[] = [
  {
    component: ContactDetails,
    entity_type_prefix: 'company_entity_details',
    navName: '', //'Contact details',
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.phone_number &&
        !!data?.entity?.company_entity_details?.contact_name &&
        !!data?.entity?.preferred_contact_way;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: CompanyDetails,
    entity_type_prefix: 'company_entity_details',
    navName: 'Company details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.company_entity_details?.company_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: IdentificationDocuments,
    entity_type_prefix: 'company_entity_details',
    label: 'Please upload document',
    title: 'Company extract',
    navName: 'Company extract',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        (data?.entity?.document_files?.filter(
          item => item.type === 'company_entity_details_identification_document',
        )?.length ?? 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: TaxInformation,
    entity_type_prefix: 'company_entity_details',
    navName: 'Tax information',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = (data?.entity?.company_entity_details?.tax_residence?.length || 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'company_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'company_entity_details',
      field_name: 'is_managed_by_another_institution',
      title: 'Is this entity managed by another Financial Institution?',
      subroad: [CompanyCRS],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Investment entity status',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 1;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const completed =
        !!data?.entity?.company_entity_details?.crs_status ||
        data?.entity?.company_entity_details?.is_managed_by_another_institution === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: PaymentInstructions,
    entity_type_prefix: 'company_entity_details',
    navName: 'Payment instructions',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.bank_account_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'company_entity_details',
    person_details_key: 'directors',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'company_entity_details',
      label: 'Company extract document',
      field_name: 'directors',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Directors details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'directors',
        entity_type_prefix: 'company_entity_details',
      });
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'company_entity_details',
    person_details_key: 'shareholders',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'company_entity_details',
      label: 'Company extract document',
      field_name: 'shareholders',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Shareholders details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        entity_type_prefix: 'company_entity_details',
        person_details_key: 'shareholders',
      });
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'company_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'company_entity_details',
      field_name: 'is_advisor',
      title: 'Do you want someone else to access this account on your behalf?',
      description: 'For example: an accountant or financial advisor',
      subroad: [AdvisorAccess, AdviserDetails],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Advisor details',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 2;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const document = data?.entity?.document_files?.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      const completed =
        (!!document && !!data?.entity?.company_entity_details?.name_authorized_representative) ||
        data?.entity?.company_entity_details?.is_advisor === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'company_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'company_entity_details',
      field_name: 'is_wholesale',
      title: 'Are you Wholesale investor?',
      subroad: [WholesaleCertificate],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Wholesale investor details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const document = (data?.entity?.document_files || [])?.filter(
        item => item.type === WholesaleCertificateDocType.Certificate,
      );
      const completed =
        (document?.length > 0 && !!data?.entity?.company_entity_details?.wholesale_type) ||
        data?.entity?.company_entity_details?.is_wholesale === false;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: Confirmation,
    entity_type_prefix: 'company_entity_details',
    navName: 'Confirmation',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.status && data?.entity?.status !== EntityStatus.EntityCreation;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: CompletionInformation,
    navName: 'Completion',
    entity_type_prefix: 'company_entity_details',
    allowSkip: true,
    getNavProgress: () => {
      return {
        completed: false,
        percent: 100,
      };
    },
  },
];

export const trustRoad: OnboardingRoadItem[] = [
  {
    component: ContactDetails,
    entity_type_prefix: 'trust_entity_details',
    navName: '', //'Contact Details',
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.phone_number &&
        !!data?.entity?.trust_entity_details?.contact_name &&
        !!data?.entity?.preferred_contact_way;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: TrustDetails,
    entity_type_prefix: 'trust_entity_details',
    navName: 'Trust details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.trust_entity_details?.company_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: IdentificationDocuments,
    entity_type_prefix: 'trust_entity_details',
    label: 'Please upload document',
    title: 'Trust deed document',
    navName: 'Trust deed document',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        (data?.entity?.document_files?.filter(
          item => item.type === `trust_entity_details_identification_document`,
        )?.length ?? 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: TaxInformation,
    entity_type_prefix: 'trust_entity_details',
    navName: 'Tax information',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = (data?.entity?.trust_entity_details?.tax_residence?.length || 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'trust_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'trust_entity_details',
      field_name: 'is_managed_by_another_institution',
      title: 'Is this entity managed by another Financial Institution?',
      subroad: [CompanyCRS],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Investment entity status',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 1;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const completed =
        !!data?.entity?.trust_entity_details?.crs_status ||
        data?.entity?.trust_entity_details?.is_managed_by_another_institution === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: PaymentInstructions,
    entity_type_prefix: 'trust_entity_details',
    navName: 'Payment instructions',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.bank_account_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'trust_entity_details',
    person_details_key: 'trustee',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'trust_entity_details',
      label: 'Company extract document',
      field_name: 'trustee',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Trustee details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'trustee',
        entity_type_prefix: 'trust_entity_details',
      });
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'trust_entity_details',
    person_details_key: 'beneficiaries',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'trust_entity_details',
      label: 'Company extract document',
      field_name: 'beneficiaries',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Beneficiaries details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'beneficiaries',
        entity_type_prefix: 'trust_entity_details',
      });
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'trust_entity_details',
    person_details_key: 'settlors',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'trust_entity_details',
      label: 'Company extract document',
      field_name: 'settlors',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Settlors details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'settlors',
        entity_type_prefix: 'trust_entity_details',
      });
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'trust_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'trust_entity_details',
      field_name: 'is_advisor',
      title: 'Do you want someone else to access this account on your behalf?',
      description: 'For example: an accountant or financial advisor',
      subroad: [AdvisorAccess, AdviserDetails],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Advisor details',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 2;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const document = data?.entity?.document_files?.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      const completed =
        (!!document && !!data?.entity?.trust_entity_details?.name_authorized_representative) ||
        data?.entity?.trust_entity_details?.is_advisor === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'trust_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'trust_entity_details',
      field_name: 'is_wholesale',
      title: 'Are you Wholesale investor?',
      subroad: [WholesaleCertificate],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Wholesale investor details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const document = (data?.entity?.document_files || [])?.filter(
        item => item.type === WholesaleCertificateDocType.Certificate,
      );
      const completed =
        (document?.length > 0 && !!data?.entity?.trust_entity_details?.wholesale_type) ||
        data?.entity?.trust_entity_details?.is_wholesale === false;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: Confirmation,
    entity_type_prefix: 'trust_entity_details',
    navName: 'Confirmation',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.status && data?.entity?.status !== EntityStatus.EntityCreation;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: CompletionInformation,
    navName: 'Completion',
    entity_type_prefix: 'trust_entity_details',
    allowSkip: true,
    getNavProgress: () => {
      return {
        completed: false,
        percent: 100,
      };
    },
  },
];

export const smsfRoad: OnboardingRoadItem[] = [
  {
    component: ContactDetails,
    entity_type_prefix: 'smsf_entity_details',
    navName: '', //'Contact Details',
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.phone_number &&
        !!data?.entity?.smsf_entity_details?.contact_name &&
        !!data?.entity?.preferred_contact_way;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: SmsfDetails,
    entity_type_prefix: 'smsf_entity_details',
    navName: 'Self Managed Superannuation Fund details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.smsf_entity_details?.company_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: IdentificationDocuments,
    entity_type_prefix: 'smsf_entity_details',
    label: 'Please upload document',
    title: 'Trust deed document',
    navName: 'Trust deed document',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        (data?.entity?.document_files?.filter(
          item => item.type === `smsf_entity_details_identification_document`,
        )?.length ?? 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: TaxInformation,
    entity_type_prefix: 'smsf_entity_details',
    navName: 'Tax information',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = (data?.entity?.smsf_entity_details?.tax_residence?.length || 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'smsf_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'smsf_entity_details',
      field_name: 'is_managed_by_another_institution',
      title: 'Is this entity managed by another Financial Institution?',
      subroad: [CompanyCRS],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Investment entity status',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 1;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const completed =
        !!data?.entity?.smsf_entity_details?.crs_status ||
        data?.entity?.smsf_entity_details?.is_managed_by_another_institution === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: PaymentInstructions,
    entity_type_prefix: 'smsf_entity_details',
    navName: 'Payment instructions',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.bank_account_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'smsf_entity_details',
    person_details_key: 'trustee',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'smsf_entity_details',
      label: 'Company extract document',
      field_name: 'trustee',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Trustee details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'trustee',
        entity_type_prefix: 'smsf_entity_details',
      });
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'smsf_entity_details',
    person_details_key: 'beneficiaries',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'smsf_entity_details',
      label: 'Company extract document',
      field_name: 'beneficiaries',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Beneficiaries details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'beneficiaries',
        entity_type_prefix: 'smsf_entity_details',
      });
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'smsf_entity_details',
    person_details_key: 'settlors',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'smsf_entity_details',
      label: 'Company extract document',
      field_name: 'settlors',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Settlors details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'settlors',
        entity_type_prefix: 'smsf_entity_details',
      });
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'smsf_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'smsf_entity_details',
      field_name: 'is_advisor',
      title: 'Do you want someone else to access this account on your behalf?',
      description: 'For example: an accountant or financial advisor',
      subroad: [AdvisorAccess, AdviserDetails],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Advisor details',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 2;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const document = data?.entity?.document_files?.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      const completed =
        (!!document && !!data?.entity?.smsf_entity_details?.name_authorized_representative) ||
        data?.entity?.smsf_entity_details?.is_advisor === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'smsf_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'smsf_entity_details',
      field_name: 'is_wholesale',
      title: 'Are you Wholesale investor?',
      subroad: [WholesaleCertificate],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Wholesale investor details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const document = (data?.entity?.document_files || [])?.filter(
        item => item.type === WholesaleCertificateDocType.Certificate,
      );
      const completed =
        (document?.length > 0 && !!data?.entity?.smsf_entity_details?.wholesale_type) ||
        data?.entity?.smsf_entity_details?.is_wholesale === false;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: Confirmation,
    entity_type_prefix: 'smsf_entity_details',
    navName: 'Confirmation',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.status && data?.entity?.status !== EntityStatus.EntityCreation;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: CompletionInformation,
    navName: 'Completion',
    entity_type_prefix: 'smsf_entity_details',
    allowSkip: true,
    getNavProgress: () => {
      return {
        completed: false,
        percent: 100,
      };
    },
  },
];

export const partnershipRoad: OnboardingRoadItem[] = [
  {
    component: ContactDetails,
    entity_type_prefix: 'partnership_entity_details',
    navName: '', //'Contact details',
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.phone_number &&
        !!data?.entity?.partnership_entity_details?.contact_name &&
        !!data?.entity?.preferred_contact_way;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: PartnershipDetails,
    entity_type_prefix: 'partnership_entity_details',
    navName: 'Partnership details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.partnership_entity_details?.company_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: IdentificationDocuments,
    entity_type_prefix: 'partnership_entity_details',
    label: 'Please upload document',
    title: 'Partnership agreement',
    navName: 'Partnership agreement',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        (data?.entity?.document_files?.filter(
          item => item.type === `partnership_entity_details_identification_document`,
        )?.length ?? 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: TaxInformation,
    entity_type_prefix: 'partnership_entity_details',
    navName: 'Tax information',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = (data?.entity?.partnership_entity_details?.tax_residence?.length || 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'partnership_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'partnership_entity_details',
      field_name: 'is_managed_by_another_institution',
      title: 'Is this entity managed by another Financial Institution?',
      subroad: [CompanyCRS],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Investment entity status',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 1;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const completed =
        !!data?.entity?.partnership_entity_details?.crs_status ||
        data?.entity?.partnership_entity_details?.is_managed_by_another_institution === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: PaymentInstructions,
    entity_type_prefix: 'partnership_entity_details',
    navName: 'Payment instructions',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.bank_account_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'partnership_entity_details',
    person_details_key: 'stakeholders',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'partnership_entity_details',
      label: 'Company extract document',
      field_name: 'stakeholders',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Stakeholders details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'stakeholders',
        entity_type_prefix: 'partnership_entity_details',
      });
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'partnership_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'partnership_entity_details',
      field_name: 'is_advisor',
      title: 'Do you want someone else to access this account on your behalf?',
      description: 'For example: an accountant or financial advisor',
      subroad: [AdvisorAccess, AdviserDetails],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Advisor details',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 2;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const document = data?.entity?.document_files?.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      const completed =
        (!!document &&
          !!data?.entity?.partnership_entity_details?.name_authorized_representative) ||
        data?.entity?.partnership_entity_details?.is_advisor === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'partnership_entity_details',
    optionalStepsProps: {
      entity_type_prefix: 'partnership_entity_details',
      field_name: 'is_wholesale',
      title: 'Are you Wholesale investor?',
      subroad: [WholesaleCertificate],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Wholesale investor details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const document = (data?.entity?.document_files || [])?.filter(
        item => item.type === WholesaleCertificateDocType.Certificate,
      );
      const completed =
        (document?.length > 0 && !!data?.entity?.partnership_entity_details?.wholesale_type) ||
        data?.entity?.partnership_entity_details?.is_wholesale === false;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: Confirmation,
    entity_type_prefix: 'partnership_entity_details',
    navName: 'Confirmation',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.status && data?.entity?.status !== EntityStatus.EntityCreation;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: CompletionInformation,
    navName: 'Completion',
    entity_type_prefix: 'partnership_entity_details',
    allowSkip: true,
    getNavProgress: () => {
      return {
        completed: false,
        percent: 100,
      };
    },
  },
];

export const associationRoad: OnboardingRoadItem[] = [
  {
    component: ContactDetails,
    entity_type_prefix: 'association_entity_details_id',
    navName: '', //'Contact details',
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.phone_number &&
        !!data?.entity?.association_entity_details_id?.contact_name &&
        !!data?.entity?.preferred_contact_way;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: AssociationDetails,
    entity_type_prefix: 'association_entity_details_id',
    navName: 'Association details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.association_entity_details_id?.company_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: TaxInformation,
    entity_type_prefix: 'association_entity_details_id',
    navName: 'Tax information',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        (data?.entity?.association_entity_details_id?.tax_residence?.length || 0) > 0;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'association_entity_details_id',
    optionalStepsProps: {
      entity_type_prefix: 'association_entity_details_id',
      field_name: 'is_managed_by_another_institution',
      title: 'Is this entity managed by another Financial Institution?',
      subroad: [CompanyCRS],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Investment entity status',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 1;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const completed =
        !!data?.entity?.association_entity_details_id?.crs_status ||
        data?.entity?.association_entity_details_id?.is_managed_by_another_institution === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: PaymentInstructions,
    entity_type_prefix: 'association_entity_details_id',
    navName: 'Payment instructions',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed = !!data?.entity?.bank_account_name;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'association_entity_details_id',
    person_details_key: 'officers',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'association_entity_details_id',
      label: 'Company extract document',
      field_name: 'officers',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Officers details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'officers',
        entity_type_prefix: 'association_entity_details_id',
      });
    },
  },
  {
    component: MappedTrustee,
    entity_type_prefix: 'association_entity_details_id',
    person_details_key: 'shareholders',
    label: 'Please upload document',
    optionalStepsProps: {
      entity_type_prefix: 'association_entity_details_id',
      label: 'Company extract document',
      field_name: 'shareholders',
      title: '',
      subroad: [],
      options: [],
    },
    navName: 'Shareholders details',
    allowSkip: true,
    getNavProgress: props => {
      return getNavProgressShareholder({
        ...props,
        person_details_key: 'shareholders',
        entity_type_prefix: 'association_entity_details_id',
      });
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'association_entity_details_id',
    optionalStepsProps: {
      entity_type_prefix: 'association_entity_details_id',
      field_name: 'is_advisor',
      title: 'Do you want someone else to access this account on your behalf?',
      description: 'For example: an accountant or financial advisor',
      subroad: [AdvisorAccess, AdviserDetails],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Advisor details',
    allowSkip: true,
    getNavProgress: ({ data, getSubStep, step }) => {
      const allSubStep = 2;
      const currentSubStep = getSubStep(step);
      const percent = currentSubStep === 0 ? 100 : ((currentSubStep + 1) / (allSubStep + 1)) * 100;
      const document = data?.entity?.document_files?.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      const completed =
        (!!document &&
          !!data?.entity?.association_entity_details_id?.name_authorized_representative) ||
        data?.entity?.association_entity_details_id?.is_advisor === false;
      return {
        percent,
        completed,
      };
    },
  },
  {
    component: OptionalSteps,
    entity_type_prefix: 'association_entity_details_id',
    optionalStepsProps: {
      entity_type_prefix: 'association_entity_details_id',
      field_name: 'is_wholesale',
      title: 'Are you Wholesale investor?',
      subroad: [WholesaleCertificate],
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navName: 'Wholesale investor details',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const document = (data?.entity?.document_files || [])?.filter(
        item => item.type === WholesaleCertificateDocType.Certificate,
      );
      const completed =
        (document?.length > 0 && !!data?.entity?.association_entity_details_id?.wholesale_type) ||
        data?.entity?.association_entity_details_id?.is_wholesale === false;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: Confirmation,
    entity_type_prefix: 'association_entity_details_id',
    navName: 'Confirmation',
    allowSkip: true,
    getNavProgress: ({ data }) => {
      const completed =
        !!data?.entity?.status && data?.entity?.status !== EntityStatus.EntityCreation;
      return {
        percent: 100,
        completed,
      };
    },
  },
  {
    component: CompletionInformation,
    navName: 'Completion',
    entity_type_prefix: 'association_entity_details_id',
    allowSkip: true,
    getNavProgress: () => {
      return {
        completed: false,
        percent: 100,
      };
    },
  },
];
